/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
'use client';

import { useEffect, useMemo, useState } from 'react';
import { useFetcher } from 'react-router';
import { ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/forms/label';
import { RadioGroup, RadioGroupItem } from '@/components/forms/radio-group';
import { useBasket } from '@/providers/basket';
import { Link } from '@/components/link';
import { cn } from '@/lib/utils';
import { CHECKOUT_ACTION_INTENTS } from '@/components/checkout/utils/checkout-context-types';
import CheckoutErrorBanner from './checkout-error-banner';
import type { CheckoutActionData } from '../types';

/**
 * CHECKOUT-08 — Mattress Recycling Opt-In (California shoppers).
 *
 * Reads eligibility booleans surfaced by the
 * `dw.ocapi.shop.basket.modifyGETResponse` hook (via `MattressRecyclingHelper.enrichBasketResponse`):
 *   - `c_mattressRecyclingOptInRequired` — true only for CA shoppers with a
 *     mattress PLI; gates whether this component renders at all.
 *   - `c_mattressRecyclingEligible` — true for CA/OR/RI/CT shoppers with a
 *     mattress PLI; informational only — used by the order summary.
 *   - `c_mattressRecyclingPickup` — shopper's stored Yes/No selection (or
 *     `null` if none yet); seeds the radio default.
 *
 * The shopper's selection is collected for curbside-pickup logistics ONLY.
 * The state-mandated `RecycleFee` price adjustment is applied server-side by
 * `MattressRecyclingHelper.applyRecycleFees` regardless of this selection.
 */
type BasketWithMattressFlags = {
    c_mattressRecyclingOptInRequired?: boolean;
    c_mattressRecyclingPickup?: boolean | null;
};

const PICKUP_FETCHER_KEY = 'mattress-recycling-pickup';

export default function MattressRecyclingOptIn() {
    const basket = useBasket() as (ReturnType<typeof useBasket> & BasketWithMattressFlags) | undefined;
    const fetcher = useFetcher<CheckoutActionData>({ key: PICKUP_FETCHER_KEY });
    const { t } = useTranslation('checkout');

    const optInRequired = basket?.c_mattressRecyclingOptInRequired === true;
    const storedPickup = basket?.c_mattressRecyclingPickup;

    // Optimistic UI: snap the radio immediately on click while the PATCH is in
    // flight, then sync back to the server's truth when fetcher data arrives.
    // We initialize from the stored basket value so a checkout reload shows
    // the persisted selection without flicker.
    const initialValue: 'yes' | 'no' | undefined = useMemo(() => {
        if (storedPickup === true) return 'yes';
        if (storedPickup === false) return 'no';
        return undefined;
    }, [storedPickup]);

    const [selected, setSelected] = useState<'yes' | 'no' | undefined>(initialValue);

    useEffect(() => {
        // When the basket revalidates with a new stored value, sync local state.
        setSelected(initialValue);
    }, [initialValue]);

    // Don't render anything for non-CA / non-mattress baskets. The calc
    // pipeline still applies the RecycleFee for OR/RI/CT and the order
    // summary surfaces it from `priceAdjustments` — this component is
    // *only* the CA pickup opt-in.
    if (!optInRequired) {
        return null;
    }

    const errorMessage =
        fetcher.data?.step === 'mattressRecyclingPickup' && !fetcher.data?.success ? fetcher.data?.error : undefined;

    const handleChange = (next: string) => {
        if (next !== 'yes' && next !== 'no') return;
        if (next === selected) return;
        setSelected(next);

        const formData = new FormData();
        formData.append('intent', CHECKOUT_ACTION_INTENTS.MATTRESS_RECYCLING_PICKUP);
        formData.append('pickup', next === 'yes' ? 'true' : 'false');
        void fetcher.submit(formData, { method: 'post' });
    };

    return (
        <Card data-testid="mattress-recycling-opt-in" className="relative rounded-md py-8 shadow-md gap-4">
            <CardHeader className="px-8 gap-0">
                <CardTitle className="font-publico-headline font-normal text-2xl leading-9">
                    <>{t('mattressRecycling.title', { defaultValue: 'Old mattress recycling' })}</>
                </CardTitle>
            </CardHeader>
            <CardContent className="px-8 flex flex-col gap-4">
                <div>
                    <p data-testid="mattress-recycling-disclosure">
                        {t('mattressRecycling.disclosure', {
                            defaultValue:
                                'California requires a recycling fee on every mattress sold. Your delivery team can pick up your old mattress at no extra cost — just let us know below. The recycling fee applies either way.',
                        })}
                    </p>

                    <Link
                        to="/mattress-recovery-recycling-act"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-sm font-semibold uppercase tracking-wide [text-decoration-skip-ink:none] decoration-accent-secondary decoration-thin decoration-solid underline-offset-4 hover:underline focus-visible:underline">
                        {t('mattressRecycling.learnMore', { defaultValue: 'Learn more about mattress recovery' })}
                        <ExternalLink className="size-4" aria-hidden="true" />
                    </Link>
                </div>

                {errorMessage && <CheckoutErrorBanner message={errorMessage} className="text-sm" />}

                <RadioGroup
                    aria-label={t('mattressRecycling.legend', {
                        defaultValue: 'Old mattress pickup',
                    })}
                    value={selected ?? ''}
                    onValueChange={handleChange}
                    disabled={fetcher.state !== 'idle'}
                    className="gap-3">
                    <div
                        className={cn(
                            'flex items-center gap-3 rounded-md border border-border bg-background p-3',
                            selected === 'yes' && 'border-foreground'
                        )}>
                        <RadioGroupItem
                            id="mattress-recycling-pickup-yes"
                            value="yes"
                            data-testid="mattress-recycling-pickup-yes"
                        />
                        <Label htmlFor="mattress-recycling-pickup-yes" className="cursor-pointer">
                            {t('mattressRecycling.yes', {
                                defaultValue: 'Yes, please pick up my old mattress',
                            })}
                        </Label>
                    </div>
                    <div
                        className={cn(
                            'flex items-center gap-3 rounded-md border border-border bg-background p-3',
                            selected === 'no' && 'border-foreground'
                        )}>
                        <RadioGroupItem
                            id="mattress-recycling-pickup-no"
                            value="no"
                            data-testid="mattress-recycling-pickup-no"
                        />
                        <Label htmlFor="mattress-recycling-pickup-no" className="cursor-pointer">
                            {t('mattressRecycling.no', {
                                defaultValue: "No thanks, I'll dispose of it myself",
                            })}
                        </Label>
                    </div>
                </RadioGroup>
            </CardContent>
        </Card>
    );
}
